<script setup lang="ts">
import { defineProps } from 'vue';
import { twMerge } from 'tailwind-merge';
import IconButton from '../../common/IconButton.vue';
import { Draft, DraftType } from '../../../../types/draft';
import LinkIcon from '../../icons/LinkIcon.vue';
import { isSmartphone } from '../../../../constants/ui';
import MenuPoints from '../../icons/MenuPoints.vue';
import { useDraftCard } from '@/dashboard/vue/components/drafts/DraftCard/useDraftCard';
import DraftCardMenu from '@/dashboard/vue/components/drafts/DraftCard/DraftCardMenu.vue';
import DraftCardImagePreview from '@/dashboard/vue/components/drafts/DraftCard/DraftCardImagePreview.vue';
import DraftCardFilePreview from '@/dashboard/vue/components/drafts/DraftCard/DraftCardFilePreview.vue';
import { GroupMoveItems } from '@/dashboard/vue/components/public/PublicMainDraft/usePublicMainDraft';

const props = defineProps<{
  draft: Draft;
  checked: boolean;
  mobileColumns: boolean;
  canDelete: boolean;
  canMove: boolean;
  groupMoveItems: GroupMoveItems[];
  checkAction: (draft: Draft) => void;
  deleteAction: (draft: Draft) => void;
  moveAction: (draft: Draft, projectId: number) => void;
}>();

const { card, menu, panel, link, showMenu, showMenuAction, closeMenu, copyDraft, copyName, getMenuStyle } = useDraftCard(props.draft);
</script>

<template>
  <div
    ref="card"
    class="draft-card group relative flex h-[204px] flex-col rounded-12 bg-gray-250 dark:bg-gray-950 laptop:h-[280px]"
    :class="{ 'no-read': draft.unread }"
  >
    <a
      :href="link"
      :target="isSmartphone ? '' : '_blank'"
      class="flex h-[calc(100%-56px)] flex-1 items-center justify-center p-s !pb-zero max-tablet:group-[.mob-columns]:p-[12px]"
    >
      <draft-card-image-preview v-if="draft.draft_type !== DraftType.file" :draft="draft" />
      <draft-card-file-preview v-else-if="draft.draft_type === DraftType.file" :draft="draft" />
    </a>

    <div ref="panel" class="relative flex items-center justify-between p-[12px] max-tablet:group-[.mob-columns]:p-xs" @click="() => closeMenu()">
      <label v-if="props.canDelete || props.canMove" class="tppr-checkbox mx-xxs" @click="props.checkAction(draft)">
        <span class="tppr-checkbox__input" :class="{ checked: checked }"></span>
        <span
          class="tppr-button__animation tppr-checkbox__presenter group-hover:border-gray-900 dark:group-hover:border-gray-500 max-tablet:border-gray-900 dark:max-tablet:border-gray-500"
          :class="{ '!border-gray-900 dark:!border-gray-500': showMenu }"
        />
      </label>

      <IconButton
        class="hover:bg-white active:bg-gray-300 group-hover:stroke-gray-900 dark:group-hover:stroke-gray-500 max-tablet:stroke-gray-900 dark:max-tablet:stroke-gray-500"
        :class="twMerge(showMenu && 'stroke-gray-900 dark:stroke-gray-500', !showMenu && 'stroke-gray-450 dark:stroke-gray-700')"
        :shadow="true"
        :action="() => copyDraft()"
        :active="false"
      >
        <link-icon class="h-[20px] w-[20px]" />
      </IconButton>

      <IconButton
        class="hover:bg-white active:bg-gray-300 group-hover:stroke-gray-900 dark:group-hover:stroke-gray-500 max-tablet:stroke-gray-900 dark:max-tablet:stroke-gray-500"
        :class="twMerge(showMenu && 'stroke-gray-900 dark:stroke-gray-500', !showMenu && 'stroke-gray-450 dark:stroke-gray-700')"
        :shadow="true"
        :action="showMenuAction"
        :active="showMenu"
      >
        <MenuPoints />
      </IconButton>

      <DraftCardMenu
        v-if="showMenu"
        ref="menu"
        :draft="draft"
        :group-move-items="props.groupMoveItems"
        :can-delete="props.canDelete"
        :can-move="props.canMove"
        :copy-name-action="copyName"
        :copy-draft-action="copyDraft"
        :move-action="props.moveAction"
        :delete-action="props.deleteAction"
        :menu-style="getMenuStyle"
      />
    </div>
  </div>
</template>

<style lang="scss">
.draft-card {
  &.no-read {
    &:after {
      content: '';
      z-index: 1;
      width: 16px;
      height: 16px;
      position: absolute;
      top: -4px;
      right: -5px;
      background-color: #ff2828;
      border-radius: 50%;
      filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.15));
    }
  }

  a {
    img {
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      top: 50%;
      left: 50%;
    }
  }
}

@media screen and (max-width: 1093px) {
  //.draft-card {
  //  a {
  //    height: calc(100% - 56px);
  //  }
  //
  //  &.mob-columns {
  //    a {
  //      height: calc(100% - 48px);
  //    }
  //  }
  //}
}
</style>
